import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { apollo } from "../../../../utils/apollo";
import { useNavigate, useParams } from "react-router-dom";
import {
  CANCEL_SLASHDATA_MEMBER,
  GET_SLASHDATA_MEMBER,
  UPDATE_SLASHDATA_MEMBER,
} from "../../../../utils/graphql";
import Loading from "../../../../components/Loading";
import TextInput from "../../../../components/TextInput";
import useMemberEditStore from "../../../../store/member-edit";
import SelectInput from "../../../../components/SelectInput";
import Button from "../../../../components/Button";
import { toast } from "react-toastify";
function SlashdataMemberEditForm() {
  const { t } = useTranslation();
  const [viewOnly, setViewOnly] = useState(false);
  const [verificationPending, setVerificationPending] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const { member_uuid } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    id,
    memberRefNo,
    enrolmentIssueDate,
    enrolmentStartDate,
    unifiedNo,
    emiratesIdNo,
    visaFileNo,
    birthCertNo,
    passportNo,
    gender,
    nationalityCode,
    dateOfBirth,
    fullNameEn,
    firstNameEn,
    middleNameEn,
    lastNameEn,
    fullNameAr,
    firstNameAr,
    middleNameAr,
    lastNameAr,
    maritalStatus,
    relationWithSponsor,
    memberIdType,
    memberType,
    sponsorIdNo,
    sponsorIdType,
    membershipCardNo,
    className,
    salaryBand,
    occupationDesc,
    emiratesOfVisaCode,
    emiratesOfLivingCode,
    memberCreationRefNo,
    cancellationDate,
    integrationStatus,
    status,
    createdAt,
    updatedAt,
    setMemberFromGraphQLRequest,
    updateMember,
  } = useMemberEditStore();
  const fetchSlashdataMember = useQuery({
    queryKey: [
      "slashdata_member",
      {
        memberUuid: member_uuid,
      },
    ],
    queryFn: async () => {
      const { data } = await apollo.query({
        query: GET_SLASHDATA_MEMBER,
        fetchPolicy: "network-only",
        variables: {
          memberUuid: member_uuid,
        },
      });
      return data?.getSlashdataMember;
    },
    keepPreviousData: false,
    refetchInterval: verificationPending ? 3000 : false,
    refetchIntervalInBackground: true,
    onSuccess: (data) => {
      setMemberFromGraphQLRequest(data);
      if (data.integrationStatus === "Canceled" || data.cancellationDate) {
        setViewOnly(true);
      }
      if (verificationPending) {
        if (["Completed", "AlreadyExists"].includes(data.integrationStatus)) {
          setVerificationPending(false);
          setVerificationResult({
            success: true,
            message: "Member verification completed",
          });
          toast.success("Member verification completed");
        } else if (
          data.integrationStatus === "Failed" ||
          data.errors?.length
        ) {
          const message = data.errors?.[0] || "Member verification failed";
          setVerificationPending(false);
          setVerificationResult({ success: false, message });
          toast.error(message);
        }
      }
    },
  });

  useEffect(() => {
    if (!verificationPending) return undefined;

    const timeout = setTimeout(() => {
      setVerificationPending(false);
      setVerificationResult({
        success: false,
        message: "Verification is taking longer than expected. Please refresh to check the latest status.",
      });
    }, 300000);

    return () => clearTimeout(timeout);
  }, [verificationPending]);

  // const member = useMemo(() => fetchSlashdataMember?.isSuccess ? fetchSlashdataMember?.data : null, [fetchSlashdataMember]);

  const cancelMemberMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apollo.mutate({
        mutation: CANCEL_SLASHDATA_MEMBER,
        variables: {
          slashdataMemberUuid: member_uuid,
        },
      });
      return data?.cancelSlashdataMember;
    },
    onError: (error) => {
      // toast.error("Recheck operation failed")
      console.error(error);
    },
    onSuccess: async (data) => {
      if (data) {
        toast.success("Member has been canceled");
        queryClient.invalidateQueries(["slashdata_member"]);
        setViewOnly(true);
        // setTimeout(() => {
        //     queryClient.invalidateQueries(['slashdata_policy_member_list']).then(() => {
        //         setTimeout(() => navigate(`/policy/view/${}`), 2000);
        //     });
        // }, 1000)
      } else {
        toast.error("Failed to cancel the member");
      }
    },
    // onSettled: () => {
    //     queryClient.invalidateQueries(['policy_list']);
    // }
  });
  const handleCancelMember = () => {
    if (window.confirm("Are you sure you want to cancel this member?")) {
      cancelMemberMutation.mutate();
    }
  };

  const updateMemberMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apollo.mutate({
        mutation: UPDATE_SLASHDATA_MEMBER,
        variables: {
          memberUuid: member_uuid,
          input: {
            enrolmentIssueDate:
              enrolmentIssueDate?.length === 16
                ? `${enrolmentIssueDate}:00`
                : enrolmentIssueDate?.slice(0, 19),
            enrolmentStartDate:
              enrolmentStartDate?.length === 16
                ? `${enrolmentStartDate}:00`
                : enrolmentStartDate?.slice(0, 19),
            unifiedNo,
            emiratesIdNo,
            visaFileNo,
            birthCertNo,
            passportNo,
            gender,
            nationalityCode,
            dateOfBirth: dateOfBirth?.slice(0, 10),
            fullNameEn,
            firstNameEn,
            middleNameEn,
            lastNameEn,
            fullNameAr,
            firstNameAr,
            middleNameAr,
            lastNameAr,
            maritalStatus,
            relationWithSponsor,
            memberIdType,
            memberType,
            sponsorIdNo,
            sponsorIdType,
            membershipCardNo,
            className,
            salaryBand,
            occupationDesc,
            emiratesOfVisaCode,
            emiratesOfLivingCode,
          },
        },
      });

      return data?.updateSlashdataMember;
    },
    onError: (error) => {
      const message =
        error?.graphQLErrors?.[0]?.message ||
        error?.message ||
        "Failed to update the member";
      toast.error(message.replace(/^Z_ERROR\/\s*/, ""));
    },
    onSuccess: async (data) => {
      if (!data) {
        toast.error("Failed to update the member");
        return;
      }

      toast.success(
        canRetry
          ? "Member saved and queued for verification"
          : "Member update submitted",
      );
      setVerificationResult(null);
      setVerificationPending(true);
      await Promise.all([
        queryClient.invalidateQueries(["slashdata_member"]),
        queryClient.invalidateQueries(["slashdata_policy_member_list"]),
        queryClient.invalidateQueries(["policy_list"]),
      ]);
    },
  });

  const handleUpdateMember = () => {
    const message = canRetry
      ? "Save the corrected information and retry this member?"
      : "Are you sure you want to update this member?";
    if (window.confirm(message)) {
      setVerificationResult(null);
      updateMemberMutation.mutate();
    }
  };

  const canRetry =
    (!memberCreationRefNo || integrationStatus === "Failed") &&
    ["Pending", "Failed"].includes(integrationStatus) &&
    !viewOnly;
  const canUpdate =
    memberCreationRefNo &&
    ["Completed", "AlreadyExists"].includes(integrationStatus) &&
    !viewOnly;
  const canSubmit = canRetry || canUpdate;
  const policyUuid =
    fetchSlashdataMember?.data?.slashDataPolicy?.policy?.policyUuid;
  const currentError =
    verificationResult?.message || fetchSlashdataMember?.data?.errors?.[0];

  if (fetchSlashdataMember?.isLoading) return <Loading />;
  if (!memberRefNo)
    return (
      <div className="flex items-center justify-center h-40 text-gray-500 font-light text-xl">
        {t("Member not found!")}
      </div>
    );
  return (
    <div className="grid grid-cols-12 gap-3">
      {verificationPending && (
        <div className="col-span-12 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          {t("Verification in progress. This page will update automatically.")}
        </div>
      )}

      {!verificationPending && verificationResult?.success && (
        <div className="col-span-12 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          <span>{t(verificationResult.message)}</span>
          {policyUuid && (
            <Button
              onClick={() => navigate(`/policy/view/${policyUuid}`)}
              secondary
            >
              {t("Back to policy")}
            </Button>
          )}
        </div>
      )}

      {!verificationPending &&
        !verificationResult?.success &&
        currentError &&
        (verificationResult ||
          integrationStatus === "Failed" ||
          fetchSlashdataMember?.data?.errors?.length) && (
          <div className="col-span-12 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {t(currentError)}
          </div>
        )}

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="memberRefNo"
          label={t("Member Ref No")}
          id="memberRefNo"
          type="text"
          get={memberRefNo}
          set={(v) => updateMember("memberRefNo", v)}
          required={true}
          readOnly={true}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="enrolmentIssueDate"
          label={t("Enrolment Issue Date")}
          id="enrolmentIssueDate"
          type="datetime-local"
          get={enrolmentIssueDate}
          set={(v) => updateMember("enrolmentIssueDate", v)}
          format="yyyy-MM-dd"
          required={true}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="enrolmentStartDate"
          label={t("Enrolment Start Date")}
          id="enrolmentStartDate"
          type="datetime-local"
          get={enrolmentStartDate}
          set={(v) => updateMember("enrolmentStartDate", v)}
          format="yyyy-MM-dd"
          required={true}
          readOnly={!canRetry}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="unifiedNo"
          label={t("Unified No")}
          id="unifiedNo"
          type="text"
          get={unifiedNo}
          set={(v) => updateMember("unifiedNo", v)}
          required={true}
          readOnly={!canSubmit}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="emiratesIdNo"
          label={t("Emirates Id No")}
          id="emiratesIdNo"
          type="text"
          get={emiratesIdNo}
          set={(v) => updateMember("emiratesIdNo", v)}
          required={true}
          readOnly={!canSubmit}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="visaFileNo"
          label={t("Visa File No")}
          id="visaFileNo"
          type="text"
          get={visaFileNo}
          set={(v) => updateMember("visaFileNo", v)}
          required={true}
          readOnly={!canSubmit}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="birthCertNo"
          label={t("Birth Cert No")}
          id="birthCertNo"
          type="text"
          get={birthCertNo}
          set={(v) => updateMember("birthCertNo", v)}
          required={true}
          readOnly={!canSubmit}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="passportNo"
          label={t("Passport No")}
          id="passportNo"
          type="text"
          get={passportNo}
          set={(v) => updateMember("passportNo", v)}
          required={true}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <SelectInput
          name="gender"
          label={t("Gender")}
          id="gender"
          get={gender}
          set={(v) => updateMember("gender", v)}
          required={true}
        >
          <option value="0">{t("Female")}</option>
          <option value="1">{t("Male")}</option>
        </SelectInput>
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="nationalityCode"
          label={t("Nationality Code")}
          id="nationalityCode"
          type="text"
          get={nationalityCode}
          set={(v) => updateMember("nationalityCode", v)}
          required={true}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="dateOfBirth"
          label={t("Date Of Birth")}
          id="dateOfBirth"
          type="date"
          get={dateOfBirth}
          set={(v) => updateMember("dateOfBirth", v)}
          format="yyyy-MM-dd"
          required={true}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="fullNameEn"
          label={t("Full Name En")}
          id="fullNameEn"
          type="text"
          get={fullNameEn}
          set={(v) => updateMember("fullNameEn", v)}
          required={true}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="firstNameEn"
          label={t("First Name En")}
          id="firstNameEn"
          type="text"
          get={firstNameEn}
          set={(v) => updateMember("firstNameEn", v)}
          required={true}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="middleNameEn"
          label={t("Middle Name En")}
          id="middleNameEn"
          type="text"
          get={middleNameEn}
          set={(v) => updateMember("middleNameEn", v)}
          required={true}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="lastNameEn"
          label={t("Last Name En")}
          id="lastNameEn"
          type="text"
          get={lastNameEn}
          set={(v) => updateMember("lastNameEn", v)}
          required={true}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="fullNameAr"
          label={t("Full Name Ar")}
          id="fullNameAr"
          type="text"
          get={fullNameAr}
          set={(v) => updateMember("fullNameAr", v)}
          required={true}
          readOnly={!canSubmit}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="firstNameAr"
          label={t("First Name Ar")}
          id="firstNameAr"
          type="text"
          get={firstNameAr}
          set={(v) => updateMember("firstNameAr", v)}
          required={true}
          readOnly={!canSubmit}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="middleNameAr"
          label={t("Middle Name Ar")}
          id="middleNameAr"
          type="text"
          get={middleNameAr}
          set={(v) => updateMember("middleNameAr", v)}
          required={true}
          readOnly={!canSubmit}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="lastNameAr"
          label={t("Last Name Ar")}
          id="lastNameAr"
          type="text"
          get={lastNameAr}
          set={(v) => updateMember("lastNameAr", v)}
          required={true}
          readOnly={!canSubmit}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <SelectInput
          name="maritalStatus"
          label={t("Marital Status")}
          id="maritalStatus"
          get={maritalStatus}
          set={(v) => updateMember("maritalStatus", v)}
          required={true}
        >
          <option value="1">{t("Married")}</option>
          <option value="2">{t("Single")}</option>
        </SelectInput>
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <SelectInput
          name="relationWithSponsor"
          label={t("Relation With Sponsor")}
          id="relationWithSponsor"
          get={relationWithSponsor}
          set={(v) => updateMember("relationWithSponsor", v)}
          required={true}
        >
          <option value="1">{t("Applicant")}</option>
          <option value="2">{t("Employee")}</option>
          <option value="3">{t("Spouse")}</option>
          <option value="4">{t("Child")}</option>
          <option value="5">{t("Father")}</option>
          <option value="6">{t("Mother")}</option>
          <option value="7">{t("Sister")}</option>
          <option value="8">{t("Brother")}</option>
          <option value="9">{t("Domestic Worker")}</option>
          <option value="10">{t("Other")}</option>
        </SelectInput>
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <SelectInput
          name="memberIdType"
          label={t("Member Id Type")}
          id="memberIdType"
          get={memberIdType}
          set={(v) => updateMember("memberIdType", v)}
          required={true}
          disabled={!canSubmit}
        >
          <option value="1">{t("Unified Number")}</option>
          <option value="2">{t("Emirates Id Number")}</option>
          <option value="3">{t("Visa File No")}</option>
          <option value="4">{t("Passport Number")}</option>
          <option value="5">{t("Birth Certificate Number")}</option>
        </SelectInput>
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <SelectInput
          name="memberType"
          label={t("Member Type")}
          id="memberType"
          type="text"
          get={memberType}
          set={(v) => updateMember("memberType", v)}
          required={true}
        >
          <option value="1">{t("Primary")}</option>
          <option value="2">{t("Dependent")}</option>
        </SelectInput>
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="sponsorIdNo"
          label={t("Sponsor Id No")}
          id="sponsorIdNo"
          get={sponsorIdNo}
          set={(v) => updateMember("sponsorIdNo", v)}
          required={true}
          readOnly={!canSubmit}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <SelectInput
          name="sponsorIdType"
          label={t("Sponsor Id Type")}
          id="sponsorIdType"
          get={sponsorIdType}
          set={(v) => updateMember("sponsorIdType", v)}
          required={true}
          disabled={!canSubmit}
        >
          <option value="1">{t("Unified Number")}</option>
          <option value="2">{t("Emirates Id Number")}</option>
          <option value="3">{t("Visa File No")}</option>
          <option value="4">{t("Passport No")}</option>
        </SelectInput>
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="membershipCardNo"
          label={t("Membership Card No")}
          id="membershipCardNo"
          type="text"
          get={membershipCardNo}
          set={(v) => updateMember("membershipCardNo", v)}
          required={true}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="className"
          label={t("Class Name")}
          id="className"
          type="text"
          get={className}
          set={(v) => updateMember("className", v)}
          required={true}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="salaryBand"
          label={t("Salary Band")}
          id="salaryBand"
          type="text"
          get={salaryBand}
          set={(v) => updateMember("salaryBand", v)}
          required={true}
          readOnly={!canRetry}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <TextInput
          name="occupationDesc"
          label={t("Occupation Desc")}
          id="occupationDesc"
          type="text"
          get={occupationDesc}
          set={(v) => updateMember("occupationDesc", v)}
          required={true}
        />
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <SelectInput
          name="emiratesOfVisaCode"
          label={t("Emirates Of Visa Code")}
          id="emiratesOfVisaCode"
          get={emiratesOfVisaCode}
          set={(v) => updateMember("emiratesOfVisaCode", v)}
          required={true}
        >
          <option value="" disabled>{t("Select emirate")}</option>
          <option value="1">{t("Abu Dhabi")}</option>
          <option value="2">{t("Dubai")}</option>
          <option value="3">{t("Sharjah")}</option>
          <option value="4">{t("Ajman")}</option>
          <option value="5">{t("Umm Al Quwain")}</option>
          <option value="6">{t("Ras Al Khaimah")}</option>
          <option value="7">{t("Fujairah")}</option>
        </SelectInput>
      </div>

      <div className="col-span-12 sm:col-span-6 lg:col-span-4 2xl:col-span-3">
        <SelectInput
          name="emiratesOfLivingCode"
          label={t("Emirates Of Living Code")}
          id="emiratesOfLivingCode"
          get={emiratesOfLivingCode}
          set={(v) => updateMember("emiratesOfLivingCode", v)}
          required={true}
        >
          <option value="" disabled>{t("Select emirate")}</option>
          <option value="1">{t("Abu Dhabi")}</option>
          <option value="2">{t("Dubai")}</option>
          <option value="3">{t("Sharjah")}</option>
          <option value="4">{t("Ajman")}</option>
          <option value="5">{t("Umm Al Quwain")}</option>
          <option value="6">{t("Ras Al Khaimah")}</option>
          <option value="7">{t("Fujairah")}</option>
        </SelectInput>
      </div>

      <div className="col-span-12 flex gap-3">
        {canSubmit && (
          <Button
            onClick={handleUpdateMember}
            disabled={updateMemberMutation.isLoading || verificationPending}
          >
            {verificationPending
              ? t("Verifying...")
              : updateMemberMutation.isLoading
              ? t(canRetry ? "Saving..." : "Updating...")
              : t(canRetry ? "Save and Retry" : "Update Member")}
          </Button>
        )}

        {!verificationPending &&
          ["Completed", "AlreadyExists"].includes(integrationStatus) && (
          <Button
            onClick={handleCancelMember}
            danger
          >
            {t("Cancel Member")}
          </Button>
        )}
      </div>
    </div>
  );
}
export default SlashdataMemberEditForm;
